import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WATCHLIST_STORAGE_VERSION,
  createWatchlistEntry,
  normalizeWatchlistEntries,
  getWatchlistAnnouncement,
  normalizeWatchlistId,
} from "../utils/watchlist";
import { announce, ensureLiveRegion } from "../utils/announce";
import { trackEvent } from "../utils/analytics";

const STORAGE_KEY = "tv-releases-watchlist";
const WATCHLIST_UPDATED_EVENT = "tv-releases-watchlist-updated";

const entriesAreEqual = (first, second) => JSON.stringify(first) === JSON.stringify(second);

const readStoredWatchlist = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return normalizeWatchlistEntries(parsed);
    }
    if (parsed && Array.isArray(parsed.shows)) {
      return normalizeWatchlistEntries(parsed.shows);
    }
  } catch (error) {
    console.error("Failed to read watchlist:", error);
  }

  return [];
};

export const useWatchlist = () => {
  const [entries, setEntries] = useState([]);
  const [hasHydrated, setHasHydrated] = useState(false);

  const watchlistIds = useMemo(() => {
    return new Set(entries.map((entry) => normalizeWatchlistId(entry)));
  }, [entries]);

  // The callbacks below keep a stable identity and read the current ids from here.
  const idsRef = useRef(watchlistIds);
  useEffect(() => {
    idsRef.current = watchlistIds;
  }, [watchlistIds]);

  const isInWatchlist = useCallback(
    (showOrId) => watchlistIds.has(normalizeWatchlistId(showOrId)),
    [watchlistIds]
  );

  // `silent` leaves the announcement to the caller, which words it for its own context.
  const removeFromWatchlist = useCallback((showOrId, { silent = false } = {}) => {
    const normalized = normalizeWatchlistId(showOrId);
    if (!normalized) {
      return;
    }

    trackEvent("watchlist_remove", {
      show_slug: showOrId?.slug ?? normalized,
      show_title: showOrId?.title ?? undefined,
    });
    if (!silent && idsRef.current.has(normalized)) {
      announce(getWatchlistAnnouncement(showOrId, false));
    }
    setEntries((prev) => prev.filter((entry) => normalizeWatchlistId(entry) !== normalized));
  }, []);

  const addToWatchlist = useCallback((showOrId, { silent = false } = {}) => {
    const normalized = normalizeWatchlistId(showOrId);
    if (!normalized) {
      return;
    }

    if (!silent && !idsRef.current.has(normalized)) {
      announce(getWatchlistAnnouncement(showOrId, true));
    }
    setEntries((prev) => {
      const existing = prev.find((entry) => normalizeWatchlistId(entry) === normalized) || null;
      const nextEntry = createWatchlistEntry(showOrId, existing);
      if (!nextEntry) {
        return prev;
      }
      if (existing) {
        return prev.map((entry) => (normalizeWatchlistId(entry) === normalized ? nextEntry : entry));
      }
      trackEvent("watchlist_add", {
        show_slug: showOrId?.slug ?? normalized,
        show_title: showOrId?.title ?? undefined,
      });
      return [...prev, nextEntry];
    });
  }, []);

  const toggleWatchlist = useCallback((showOrId) => {
    const normalized = normalizeWatchlistId(showOrId);
    if (!normalized) {
      return;
    }

    announce(getWatchlistAnnouncement(showOrId, !idsRef.current.has(normalized)));
    setEntries((prev) => {
      const existing = prev.find((entry) => normalizeWatchlistId(entry) === normalized) || null;
      const exists = Boolean(existing);
      if (exists) {
        return prev.filter((entry) => normalizeWatchlistId(entry) !== normalized);
      }
      const nextEntry = createWatchlistEntry(showOrId);
      return nextEntry ? [...prev, nextEntry] : prev;
    });
  }, []);

  const clearWatchlist = useCallback(() => {
    setEntries([]);
  }, []);

  // Puts back a list exactly as it was (entries and order), for Undo after Clear all.
  const restoreWatchlist = useCallback((previousEntries) => {
    setEntries(previousEntries);
  }, []);

  const upsertWatchlistEntries = useCallback((shows = []) => {
    const candidates = Array.isArray(shows) ? shows : [shows];
    const byId = new Map(
      candidates
        .map((show) => [normalizeWatchlistId(show), show])
        .filter(([id, show]) => id && show && typeof show === "object")
    );

    if (!byId.size) {
      return;
    }

    setEntries((prev) => {
      let changed = false;
      const nextEntries = prev.map((entry) => {
        const normalized = normalizeWatchlistId(entry);
        const show = byId.get(normalized);
        if (!show) {
          return entry;
        }

        const nextEntry = createWatchlistEntry(show, entry);
        if (JSON.stringify(nextEntry) !== JSON.stringify(entry)) {
          changed = true;
          return nextEntry;
        }
        return entry;
      });

      return changed ? nextEntries : prev;
    });
  }, []);

  const mergeWatchlistImport = useCallback((importedShows = []) => {
    const candidates = Array.isArray(importedShows) ? importedShows : [];
    let added = 0;
    let skipped = 0;

    setEntries((prev) => {
      const existingIds = new Set(prev.map((e) => normalizeWatchlistId(e)));
      const toAdd = [];

      for (const show of candidates) {
        const id = normalizeWatchlistId(show);
        if (!id) continue;
        if (existingIds.has(id)) {
          skipped++;
        } else {
          const entry = createWatchlistEntry(show);
          if (entry) {
            toAdd.push(entry);
            added++;
          }
        }
      }

      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });

    return { added, skipped };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    ensureLiveRegion();
    const rafId = window.requestAnimationFrame(() => {
      setEntries(readStoredWatchlist());
      setHasHydrated(true);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncEntries = () => {
      const nextEntries = readStoredWatchlist();
      setEntries((prev) => (entriesAreEqual(prev, nextEntries) ? prev : nextEntries));
    };

    const handleStorage = (event) => {
      if (event.key === STORAGE_KEY) {
        syncEntries();
      }
    };

    window.addEventListener(WATCHLIST_UPDATED_EVENT, syncEntries);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(WATCHLIST_UPDATED_EVENT, syncEntries);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!hasHydrated || typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: WATCHLIST_STORAGE_VERSION,
          shows: entries,
        })
      );
      window.dispatchEvent(new Event(WATCHLIST_UPDATED_EVENT));
    } catch (error) {
      console.error("Failed to persist watchlist:", error);
    }
  }, [entries, hasHydrated]);

  return {
    entries,
    hasHydrated,
    watchlistIds,
    watchlistCount: entries.length,
    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    toggleWatchlist,
    clearWatchlist,
    restoreWatchlist,
    upsertWatchlistEntries,
    mergeWatchlistImport,
  };
};

export default useWatchlist;
